import { useState, useEffect } from 'react';
import { UserRole, Permission } from '../../types/auth';
import { useAppSelector } from '../../store/hooks/reduxHooks';
import { Button } from '../../components/ui/Button';
import { AlertCircle } from 'lucide-react';
import Select from 'react-select';
import { Category, getCategories } from '../../services/caregory';

// CSS for react-select error state
const reactSelectErrorStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    borderColor: 'rgb(239, 68, 68)',
    backgroundColor: 'rgb(254, 242, 242)',
    '&:hover': {
      borderColor: 'rgb(239, 68, 68)'
    }
  })
};

interface AddUserFormProps {
  onSubmit: (userData: UserFormData) => void;
  onCancel: () => void;
}

export interface UserFormData {
  name: string;
  email: string;
  mobile: string;
  role: UserRole;
  dashPermissions: Permission[];
  groups?: string[];
  lender?: string[];
  suspended?: boolean;
  activated?: boolean;
  verified?: boolean;
}

// Group permissions by section for easier UI organization
interface PermissionGroup {
  label: string;
  permissions: {
    view: Permission;
    manage?: Permission;
  }[];
}

export const AddUserForm = ({ onSubmit, onCancel }: AddUserFormProps) => {
  // Get current user role to determine which roles can be created
  const { user } = useAppSelector(state => state.auth);
  const currentUserRole = user?.role || UserRole.ADMIN;

  // Form state
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    mobile: '',
    role: '' as UserRole,
    dashPermissions: [],
    groups: []
  });

  // Categories list
  const [categories, setCategories] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  // Validation state
  const [errors, setErrors] = useState<Partial<Record<keyof UserFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidationSummary, setShowValidationSummary] = useState(false);

  // Fetch partners list when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const result = await getCategories({ page: 1, pageSize: 100 });

        const options = (result.data || []).map((cat: Category) => ({
          value: cat.code,           // value used internally
          label: `${cat.code} - ${cat.name}`, // label shown in dropdown
        }));

        setCategories(options);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);
  // Permission groups for UI organization
  const permissionGroups: PermissionGroup[] = [
    {
      label: 'Dashboard',
      permissions: [
        { view: 'view-dashboard' as Permission, manage: 'manage-dashboard' as Permission }
      ]
    },
    {
      label: 'Admin Management',
      permissions: [
        { view: 'view-manage-admin' as Permission, manage: 'manage-manage-admin' as Permission }
      ]
    },
    {
      label: 'CleverPe Supply+',
      permissions: [
        { view: 'view-cleverpe-supply+' as Permission, manage: 'manage-cleverpe-supply+' as Permission }
      ]
    },
    {
      label: 'Business Network',
      permissions: [
        { view: 'view-partner' as Permission, manage: 'manage-partner' as Permission },
        { view: 'view-merchant' as Permission, manage: 'manage-merchant' as Permission },
        { view: 'view-kycpending-merchant' as Permission },
        // { view: 'view-brand' as Permission, manage: 'manage-brand' as Permission }
      ]
    },
    {
      label: 'Customers',
      permissions: [
        { view: 'view-customer' as Permission },
        { view: 'view-store-approvals' as Permission, manage: 'manage-store-approvals' as Permission }
      ]
    },
    {
      label: 'Orders and Transactions',
      permissions: [
        // { view: 'view-scheme' as Permission, manage: 'manage-scheme' as Permission },
        // { view: 'view-product' as Permission, manage: 'manage-product' as Permission },
        { view: 'view-order' as Permission, manage: 'manage-order' as Permission },
        { view: 'view-gateway-order' as Permission, manage: 'manage-gateway-order' as Permission }
      ]
    },
    {
      label: 'Products and Schemes',
      permissions: [
        { view: 'view-scheme' as Permission, manage: 'manage-scheme' as Permission },
        { view: 'view-product' as Permission, manage: 'manage-product' as Permission },
        { view: 'view-scheme-bucket' as Permission, manage: 'manage-scheme-bucket' as Permission },
        { view: 'view-category' as Permission, manage: 'manage-category' as Permission }
      ]
    },
    {
      label: 'Tools',
      permissions: [
        { view: 'view-calculator' as Permission }
      ]
    },


    {
      label: 'Reports',
      permissions: [
        { view: 'view-pre-approvals-report' as Permission },
        { view: 'view-settlements-report' as Permission },
        { view: 'view-merchant-data-report' as Permission }
      ]
    },
    {
      label: 'Enquiries and Support',
      permissions: [
        { view: 'view-grievance-redressals' as Permission, manage: 'manage-grievance-redressals' as Permission }
      ]
    },
    {
      label: 'API Monitoring',
      permissions: [
        { view: 'view-external-api-logs' as Permission }
      ]
    },
  ];

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when field is edited
    if (errors[name as keyof UserFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handlePermissionChange = (permission: Permission, checked: boolean) => {
    const mandatoryPermissions: Permission[] = ['view-dashboard', 'manage-dashboard'];

    setFormData(prev => {
      const currentPermissions = new Set(prev.dashPermissions);

      // Always include mandatory permissions
      mandatoryPermissions.forEach(p => currentPermissions.add(p));

      if (checked) {
        currentPermissions.add(permission);
      } else {
        // Only allow removing non-mandatory permissions
        if (!mandatoryPermissions.includes(permission)) {
          currentPermissions.delete(permission);
        }
      }

      return {
        ...prev,
        dashPermissions: Array.from(currentPermissions),
      };
    });
  };

  useEffect(() => {
    const mandatoryPermissions: Permission[] = ['view-dashboard', 'manage-dashboard'];

    setFormData(prev => {
      const current = prev.dashPermissions || [];
      const updated = [...new Set([...current, ...mandatoryPermissions])];
      return { ...prev, dashPermissions: updated };
    });
  }, []);



  // Handle groups selection change
  const handleGroupsChange = (selectedOptions: any) => {
    const selectedGroups = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];

    setFormData(prev => ({
      ...prev,
      groups: selectedGroups
    }));

    // Clear error when field is edited
    if (errors.groups) {
      setErrors(prev => ({ ...prev, groups: undefined }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof UserFormData, string>> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Mobile validation
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    // Permissions validation
    if (formData.dashPermissions.length === 0) {
      newErrors.dashPermissions = 'At least one permission must be selected';
    }

    // Group permissions validation for cleverpeuser role
    if (formData.role === UserRole.CLEVERPE_USER && (!formData.groups || formData.groups.length === 0)) {
      newErrors.groups = 'At least one group permission must be selected';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setShowValidationSummary(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }

    setShowValidationSummary(false);
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Add status fields to the form data
      const completeFormData = {
        ...formData,
        suspended: false,
        activated: true,
        verified: true
      };

      // In a real app, this would be an API call
      await new Promise(resolve => setTimeout(resolve, 800));
      onSubmit(completeFormData);
    } catch (error) {
      console.error('Error creating user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get available roles based on current user role
  const getAvailableRoles = () => {
    let roles: { value: UserRole; label: string }[] = [];

    if (currentUserRole === UserRole.SUPERADMIN) {
      roles = [
        { value: UserRole.ADMIN, label: 'Admin' },
        { value: UserRole.CLEVERPE_USER, label: 'CleverPe User' },
      ];
    } else if (currentUserRole === UserRole.ADMIN) {
      roles = [
        { value: UserRole.CLEVERPE_USER, label: 'CleverPe User' },
      ];
    }

    return roles;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 no-scrollbar">
      {/* Validation Summary */}
      {showValidationSummary && Object.keys(errors).length > 0 && (
        <div className="p-4 mb-4 border border-red-200 rounded-lg bg-red-50">
          <div className="flex items-center mb-2">
            <AlertCircle size={20} className="mr-2 text-red-600" />
            <h3 className="font-medium text-red-700">Please fix the following errors:</h3>
          </div>
          <ul className="ml-8 text-sm text-red-600 list-disc">
            {Object.entries(errors).map(([field, message]) => (
              <li key={field}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-6 space-y-4">
        {/* Name field */}
        <div>
          <label htmlFor="name" className="block mb-1 text-sm font-gilroy-medium text-brand-dark">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="Enter full name"
          />
          {errors.name && (
            <p className="flex items-center mt-1 text-xs text-red-500">
              <AlertCircle size={12} className="mr-1" />
              {errors.name}
            </p>
          )}
        </div>

        {/* Email field */}
        <div>
          <label htmlFor="email" className="block mb-1 text-sm font-gilroy-medium text-brand-dark">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="Enter email address"
          />
          {errors.email && (
            <p className="flex items-center mt-1 text-xs text-red-500">
              <AlertCircle size={12} className="mr-1" />
              {errors.email}
            </p>
          )}
        </div>

        {/* Mobile field */}
        <div>
          <label htmlFor="mobile" className="block mb-1 text-sm font-gilroy-medium text-brand-dark">
            Mobile Number <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <span className="text-gray-500">+91</span>
            </div>
            <input
              id="mobile"
              name="mobile"
              type="tel"
              value={formData.mobile}
              onChange={handleChange}
              className={`w-full pl-12 pr-3 py-2 border ${errors.mobile ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter 10-digit mobile number"
              maxLength={10}
            />
          </div>
          {errors.mobile && (
            <p className="flex items-center mt-1 text-xs text-red-500">
              <AlertCircle size={12} className="mr-1" />
              {errors.mobile}
            </p>
          )}
        </div>

        {/* Role field */}
        <div>
          <label htmlFor="role" className="block mb-1 text-sm font-gilroy-medium text-brand-dark">
            Role <span className="text-red-500">*</span>
          </label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.role ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="" disabled>Select Role</option>
            {getAvailableRoles().map(role => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
          {errors.role && (
            <p className="flex items-center mt-1 text-xs text-red-500">
              <AlertCircle size={12} className="mr-1" />
              {errors.role}
            </p>
          )}
        </div>

        {/* Group Permissions field - only shown for CleverPe User role */}
        {formData.role === UserRole.CLEVERPE_USER && (
          <div>
            <label htmlFor="categories" className="block mb-1 text-sm font-gilroy-medium text-brand-dark">
              Group Category Permissions <span className="text-red-500">*</span>
            </label>
            <Select
              id="categories"
              isMulti
              options={categories} // <-- use categories here
              value={categories.filter(option => formData.groups?.includes(option.value))} // pre-select
              onChange={handleGroupsChange} // keep your existing handler
              className={errors.groups ? 'react-select-error' : ''}
              classNamePrefix="react-select"
              placeholder="Select Groups"
              isLoading={isLoadingCategories} // <-- loading for categories
              isDisabled={isLoadingCategories} // disable while loading
              noOptionsMessage={() => "No categories found"} // updated message
              styles={errors.groups ? reactSelectErrorStyles : undefined}
            />
            {errors.groups && (
              <p className="flex items-center mt-1 text-xs text-red-500">
                <AlertCircle size={12} className="mr-1" />
                {errors.groups}
              </p>
            )}
          </div>

        )}

        {/* Lender field - only shown for CleverPe User role */}
        {formData.role === UserRole.CLEVERPE_USER && (
          <div className="mt-4">
            <label htmlFor="lender" className="block mb-1 text-sm font-gilroy-medium text-brand-dark">
              Lender <span className="text-red-500">*</span>
            </label>
            <Select
              id="lender"
              isMulti
              options={[
                { label: 'Cashe', value: 'cashe' },
                { label: 'Cashe-EV', value: 'cashe-ev' },
                { label: 'Zype', value: 'zype' },
              ]}
              value={
                formData.lender?.map(l => ({
                  value: l,
                  label:
                    l === 'cashe'
                      ? 'Cashe'
                      : l === 'cashe-ev'
                        ? 'Cashe-EV'
                        : l === 'zype'
                          ? 'Zype'
                          : l
                })) || []
              }
              onChange={(selected) =>
                setFormData(prev => ({
                  ...prev,
                  lender: selected?.map(option => option.value) || [],
                }))
              }
              className={errors.lender ? 'react-select-error' : ''}
              classNamePrefix="react-select"
              placeholder="Select Lenders"
              noOptionsMessage={() => "No lenders available"}
              styles={errors.lender ? reactSelectErrorStyles : undefined}
            />
            {errors.lender && (
              <p className="flex items-center mt-1 text-xs text-red-500">
                <AlertCircle size={12} className="mr-1" />
                {errors.lender}
              </p>
            )}
          </div>
        )}


        {/* Permissions section */}
        <div className="mt-6">
          <label className="block mb-3 text-sm font-gilroy-medium text-brand-dark">
            Dashboard Permissions <span className="text-red-500">*</span>
          </label>

          {errors.dashPermissions && (
            <p className="flex items-center mt-1 mb-3 text-xs text-red-500">
              <AlertCircle size={12} className="mr-1" />
              {errors.dashPermissions}
            </p>
          )}

          <div className={`p-4 border ${errors.dashPermissions ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg`}>
            {permissionGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="mb-4 last:mb-0">
                <h3 className="pb-1 mb-2 text-sm border-b border-gray-200 font-gilroy-bold">
                  {group.label}
                </h3>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {group.permissions.map((permissionPair, index) => {
                    const isCleverUser = formData.role === UserRole.CLEVERPE_USER;

                    const alwaysSelected = ['view-dashboard', 'manage-dashboard'];
                    const autoCheckedForClever = [
                      'view-merchant', 'manage-merchant',
                      'view-order', 'manage-order',
                      'view-store-approvals', 'manage-store-approvals',
                      'view-pre-approvals-report'
                    ];

                    const isMandatoryView = alwaysSelected.includes(permissionPair.view);
                    const isMandatoryManage = permissionPair.manage ? alwaysSelected.includes(permissionPair.manage) : false;

                    // Auto-check logic (editable)
                    const viewChecked =
                      isMandatoryView ||
                      formData.dashPermissions.includes(permissionPair.view) ||
                      (isCleverUser && autoCheckedForClever.includes(permissionPair.view));

                    const manageChecked = permissionPair.manage
                      ? isMandatoryManage ||
                      formData.dashPermissions.includes(permissionPair.manage) ||
                      (isCleverUser && autoCheckedForClever.includes(permissionPair.manage))
                      : false;
                    return (
                      <div key={index} className="space-y-1">
                        <div className="flex items-center">
                          <input
                            id={`view-${permissionPair.view}`}
                            type="checkbox"
                            checked={viewChecked}
                            disabled={isMandatoryView}
                            onChange={(e) =>
                              handlePermissionChange(permissionPair.view, e.target.checked)
                            }
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label
                            htmlFor={`view-${permissionPair.view}`}
                            className={`block ml-2 text-sm text-gray-700 ${isMandatoryView ? 'opacity-60 cursor-not-allowed' : ''
                              }`}
                          >
                            View{' '}
                            {permissionPair.view
                              .replace('view-', '')
                              .split('-')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')}
                          </label>
                        </div>

                        {permissionPair.manage && (
                          <div className="flex items-center ml-6">
                            <input
                              id={`manage-${permissionPair.manage}`}
                              type="checkbox"
                              checked={manageChecked}
                              disabled={isMandatoryManage}
                              onChange={(e) => {
                                if (permissionPair.manage) {
                                  handlePermissionChange(permissionPair.manage, e.target.checked);
                                }
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label
                              htmlFor={`manage-${permissionPair.manage}`}
                              className={`block ml-2 text-sm text-gray-700 ${isMandatoryManage ? 'opacity-60 cursor-not-allowed' : ''
                                }`}
                            >
                              Manage{' '}
                              {permissionPair.manage
                                .replace('manage-', '')
                                .split('-')
                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')}
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form actions */}
      <div className="flex justify-end pt-4 space-x-3 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="px-4 py-2"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
          className="px-4 py-2"
        >
          {isSubmitting ? 'Saving...' : 'Create User'}
        </Button>
      </div>
    </form>
  );
};

export default AddUserForm; 